import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { unsetPropertyCheckProxy } from "@/utils/debug/unsetPropertyCheckProxy.ts";
import { useLingui } from "@lingui/react/macro";
import { useEffect, type ChangeEvent } from "react";
import type { NumberFieldProps, TextFieldProps } from "react-aria-components";
import {
    useFormState,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
    type UseFormReturn,
} from "react-hook-form";

/**
 * Options for {@link ariaFieldRegister} / {@link useAriaField}.
 *
 * Same as react-hook-form's {@link RegisterOptions}, except that the `required` rule is replaced with a boolean `isRequired` (the validation error message is supplied automatically).
 */
export interface AriaFieldOptions<T extends FieldValues, TName extends FieldPath<T> = FieldPath<T>> extends Omit<
    RegisterOptions<T, TName>,
    "required"
> {
    isRequired?: boolean;
}

/**
 * Register a react-aria field component with react-hook-form
 * @returns `{ref, props}` - pass `ref={ref}` to the `Input` and `{...props}` to the Field
 * @public
 */
export function ariaFieldRegister<T extends FieldValues, TName extends FieldPath<T>>(
    name: TName,
    form: UseFormReturn<T>,
    options?: AriaFieldOptions<T, TName>,
) {
    const { register, getValues } = form;
    const { isRequired, ...registerOptions } = options ?? {};
    // XXX: `Omit` flattens the discriminated union of RHF's `RegisterOptions`, so re-assert the type here
    const {
        name: registerName,
        onBlur,
        onChange,
        ref,
        required,
        disabled,
        ...rest
    } = register(name, { ...registerOptions, required: isRequired } as RegisterOptions<T, TName>);

    function onChangeWrapper(value: string) {
        type RhfOnChangeEvent = Parameters<typeof onChange>[0];
        type FakeEvent = ChangeEvent<HTMLInputElement> & RhfOnChangeEvent;

        // NB: `onChange` is called with only the value from react-aria, so we
        // need to wrap it in a fake event object for react-hook-form
        const event = unsetPropertyCheckProxy<FakeEvent>({
            type: "change",
            target: {
                type: "text",
                name: registerName,
                value,
            } satisfies Partial<HTMLInputElement>,
        });
        return onChange(event);
    }

    const optDisabled = options?.disabled ?? false;

    return {
        ref,
        props: {
            ...rest,
            // NB: Must not use "native" validation since it will break with nested forms (ie. child forms with invalid fields will prevent parent form submission)
            validationBehavior: "aria",
            defaultValue: getValues(name),
            name: registerName,
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onBlur,
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onChange: onChangeWrapper,
            isRequired: !!required || isRequired,
            isDisabled: !!disabled || optDisabled,
        } satisfies TextFieldProps | NumberFieldProps,
    } as const;
}

/**
 * Registers and gets the error message for a field.
 *
 * Automatically unregisters the field on unmount.
 *
 * @see {@link ariaFieldRegister} for detailed return value description */
export function useAriaField<T extends FieldValues, TName extends FieldPath<T>>(
    name: TName,
    form: UseFormReturn<T>,
    options?: AriaFieldOptions<T, TName>,
) {
    const { t } = useLingui();
    const { unregister } = form;
    useEffect(() => {
        // Unregister field on unmount (for optional fields)
        return () => {
            console.debug("Un-registering field %o", name);
            return unregister(name, {
                // NB: Since unregister is also called during hot reload, keep the value.
                // (If this causes any problems, a parameter should be added here to control this.)
                keepValue: true,
                // NB: Removing the default value will cause invalid `undefined` values to be submitted
                keepDefaultValue: true,
            });
        };
    }, [unregister, name]);

    const { control } = form;
    const { errors } = useFormState({ control, name });

    //#endregion Hooks

    const error = errors[name];
    const errorMessage: string | undefined =
        error != null
            ? error.type === "required"
                ? t(requiredMessage)
                : // XXX: some problem in RHF typings requires the `String()` cast
                  // eslint-disable-next-line @typescript-eslint/no-base-to-string
                  String(error.message)
            : undefined;
    // XXX: RHF does not seem to have a field-specific `isValid` field (instead it's a form-wide `isValid`), so emulate it by checking for an error message
    const isInvalid = error != null;

    const obj = ariaFieldRegister(name, form, options);
    return {
        ...obj,
        props: {
            ...obj.props,
            isInvalid,
            errorMessage,
        },
    } as const;
}
